import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import {
  getWatchBrands,
  getRecentBrandInvites,
  createBrandInvite,
  cancelBrandInvite,
  type BrandInvite,
} from '../../services/firebase';
import type { BrandMembershipRole, WatchBrand } from '../../types';
import { Button } from '../common/Button';
import { BrandFilterSelect } from '../CuratedWatches/BrandFilterSelect';

/**
 * Brand Portal invite management (Phase 1.3b) — super-admin only.
 *
 * Three sections: a create-invite form, the list of pending invites (with a
 * per-row cancel + copy-link action), and a read-only history of recently
 * resolved invites. The `brandInvites` collection is read directly; all
 * mutations go through the watchlock callables.
 */

const HISTORY_LIMIT = 20;

/** Build the magic-link accept URL for an invite. */
function acceptLinkFor(token: string): string {
  return `${window.location.origin}/brand-portal/accept-invite?token=${encodeURIComponent(token)}`;
}

/** Relative time ("in 12 days" / "3 days ago") from a Firestore Timestamp. */
function relativeFrom(ts?: { toDate: () => Date }): string {
  if (!ts || typeof ts.toDate !== 'function') return '—';
  try {
    return formatDistanceToNow(ts.toDate(), { addSuffix: true });
  } catch {
    return '—';
  }
}

function roleLabel(role: BrandMembershipRole): string {
  return role === 'brandAdmin' ? 'Brand Admin' : 'Brand Member';
}

const STATUS_STYLES: Record<string, string> = {
  consumed: 'bg-green-100 text-green-700',
  expired: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-red-100 text-red-700',
  pending: 'bg-amber-100 text-amber-700',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-block text-xs px-2 py-0.5 rounded-full capitalize ${
        STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {status}
    </span>
  );
}

async function copyToClipboard(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error('Copy failed — select and copy manually');
  }
}

export function BrandInvites() {
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(true);

  const [invites, setInvites] = useState<BrandInvite[]>([]);
  const [loadingInvites, setLoadingInvites] = useState(true);

  // Create-invite form state.
  const [email, setEmail] = useState('');
  const [brandId, setBrandId] = useState('');
  const [role, setRole] = useState<BrandMembershipRole>('brandAdmin');
  const [submitting, setSubmitting] = useState(false);

  // Set after a successful create so we can surface the shareable accept link.
  const [lastLink, setLastLink] = useState<string | null>(null);

  // Per-row cancel in flight (invite id).
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const brandNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of brands) map.set(b.id, b.name);
    return map;
  }, [brands]);

  const refreshInvites = useCallback(async () => {
    setLoadingInvites(true);
    try {
      const list = await getRecentBrandInvites();
      setInvites(list);
    } catch (err) {
      console.error('Failed to load brand invites', err);
      toast.error('Failed to load invites');
    } finally {
      setLoadingInvites(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    getWatchBrands()
      .then((list) => {
        if (!cancelled) {
          setBrands([...list].sort((a, b) => a.name.localeCompare(b.name)));
        }
      })
      .catch((err) => {
        console.error('Failed to load brands', err);
        if (!cancelled) toast.error('Failed to load brands');
      })
      .finally(() => {
        if (!cancelled) setLoadingBrands(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    void refreshInvites();
  }, [refreshInvites]);

  const pending = useMemo(
    () => invites.filter((i) => i.status === 'pending'),
    [invites],
  );
  const history = useMemo(
    () => invites.filter((i) => i.status !== 'pending').slice(0, HISTORY_LIMIT),
    [invites],
  );

  const handleSubmit = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      toast.error('Email is required');
      return;
    }
    if (!brandId) {
      toast.error('Please select a brand');
      return;
    }

    setSubmitting(true);
    setLastLink(null);
    try {
      const result = await createBrandInvite({
        email: trimmedEmail,
        brandId,
        role,
      });
      toast.success(`Invite sent to ${trimmedEmail}`);

      // Surface a shareable link: prefer a backend-built link, else a token, so
      // James can pass it on directly (handy whether or not the backend emails).
      const token = result.link ?? result.token ?? result.inviteId;
      if (result.link) {
        setLastLink(result.link);
      } else if (token) {
        setLastLink(acceptLinkFor(token));
      }

      setEmail('');
      setBrandId('');
      setRole('brandAdmin');
      await refreshInvites();
    } catch (err) {
      console.error('Failed to create brand invite', err);
      toast.error(
        err instanceof Error ? err.message : 'Failed to create invite',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (invite: BrandInvite) => {
    setCancellingId(invite.id);
    try {
      await cancelBrandInvite(invite.id);
      toast.success('Invite cancelled');
      // Drop it locally for instant feedback, then refresh for the true status.
      setInvites((prev) => prev.filter((i) => i.id !== invite.id));
      await refreshInvites();
    } catch (err) {
      console.error('Failed to cancel invite', err);
      toast.error(
        err instanceof Error ? err.message : 'Failed to cancel invite',
      );
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Brand Invites</h1>
        <p className="text-gray-500 mt-1">
          Invite a brand owner or member to the Brand Portal. They accept via a
          magic link and pick up their access automatically.
        </p>
      </div>

      {/* Create invite */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">
          Create invite
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@brand.com"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <BrandFilterSelect
            brands={brands}
            loading={loadingBrands}
            value={brandId}
            onChange={setBrandId}
            label="Brand"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as BrandMembershipRole)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="brandAdmin">Brand Admin</option>
              <option value="brandMember">Brand Member</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button onClick={handleSubmit} loading={submitting}>
            Send Invite
          </Button>
        </div>

        {lastLink && (
          <div className="mt-4 bg-brand-50 border border-brand-200 rounded-lg p-4">
            <p className="text-sm text-brand-800 mb-2 font-medium">
              Invite link
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs text-gray-700 bg-white border border-gray-200 rounded px-2 py-1.5 break-all">
                {lastLink}
              </code>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => copyToClipboard(lastLink, 'Link')}
              >
                Copy
              </Button>
            </div>
            <p className="text-xs text-brand-700 mt-2">
              Share this with the invitee if they don&apos;t receive the email.
            </p>
          </div>
        )}
      </div>

      {/* Pending invites */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">
            Pending invites
          </h2>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void refreshInvites()}
            loading={loadingInvites}
          >
            Refresh
          </Button>
        </div>
        {loadingInvites ? (
          <div className="px-6 py-10 text-center text-sm text-gray-400">
            Loading invites…
          </div>
        ) : pending.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-gray-500">
            No pending invites.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Email', 'Brand', 'Role', 'Invited By', 'Expires', ''].map(
                    (h, i) => (
                      <th
                        key={i}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pending.map((invite) => (
                  <tr key={invite.id}>
                    <td className="px-4 py-3 text-sm text-gray-900 break-all">
                      {invite.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {brandNameById.get(invite.brandId) ?? invite.brandId}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {roleLabel(invite.role)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 break-all">
                      {invite.invitedByEmail ?? invite.invitedBy ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {relativeFrom(invite.expiresAt)}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() =>
                          copyToClipboard(
                            acceptLinkFor(invite.token ?? invite.id),
                            'Link',
                          )
                        }
                        className="text-xs text-brand-600 hover:text-brand-700 mr-3"
                      >
                        Copy link
                      </button>
                      <button
                        onClick={() => handleCancel(invite)}
                        disabled={cancellingId === invite.id}
                        className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50"
                      >
                        {cancellingId === invite.id ? 'Cancelling…' : 'Cancel'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">
              Recent history
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Last {HISTORY_LIMIT} resolved invites
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Email', 'Brand', 'Role', 'Invited By', 'Status'].map(
                    (h, i) => (
                      <th
                        key={i}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {history.map((invite) => (
                  <tr key={invite.id}>
                    <td className="px-4 py-3 text-sm text-gray-900 break-all">
                      {invite.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {brandNameById.get(invite.brandId) ?? invite.brandId}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {roleLabel(invite.role)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 break-all">
                      {invite.invitedByEmail ?? invite.invitedBy ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <StatusBadge status={invite.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
